import React, { ReactNode } from 'react';
import styled from 'styled-components';
import { vars } from '../../theme';
import { mediumUp } from '../../styles/breakpoints';
import HBIconButton from '../HBIconButton/HBIconButton';
import { Close16 } from '@honeybook/icons';
import HBText from '../HBText/HBText';
import { BannerActionsBaseProps } from './HBBanner.types';
import BannerActions from './BannerActions';
import { useWindowSize } from '../../hooks/useWindowSize';
import { MustIncludeOneOfBasicTestIdProps } from '../common/internal/test-id.types';
import { useBuildTestIdInternal } from '../../hooks/useBuildTestIdInternal';

interface Props extends Omit<BannerActionsBaseProps, 'primaryActionTheme'> {
    /** Heading text displayed prominently at the top of the banner. Accepts strings or JSX for rich formatting. */
    title: ReactNode;
    /** Supporting text rendered below the title. Accepts strings or JSX for rich formatting. */
    subtitle: ReactNode;
    /** Optional visual element (e.g. an image or SVG) displayed to the right of the content area. */
    illustration?: ReactNode;
    /** Visual style of the banner. 'plain' renders a white background; 'accent' renders a branded tinted background. */
    bannerTheme: 'plain' | 'accent';
    /** Called when the dismiss button is clicked; renders the close button when provided. */
    onClose?: () => void;
    /** CSS class applied to the root element. */
    className?: string;
    /** HBButton theme for the primary action. 'primary-blue' is the default CTA style; 'secondary-solid-primary-alternative' for a softer emphasis. */
    primaryActionTheme?: Extract<
        BannerActionsBaseProps['primaryActionTheme'],
        'primary-blue' | 'secondary-solid-primary-alternative'
    >;
}

// This component is still a WIP, waiting for final design, will add stories when finalized.
const HBPromotionalBanner = ({
    title,
    subtitle,
    illustration,
    onClose,
    bannerTheme,
    className,
    testId,
    testIdName,
    primaryActionTheme = 'primary-blue',
    ...actionProps
}: Props & MustIncludeOneOfBasicTestIdProps) => {
    const { isMediumUp } = useWindowSize();

    const finalTestId = useBuildTestIdInternal({
        testId,
        identifier: testIdName,
        suffix: 'banner',
        componentName: 'HBPromotionalBanner'
    });

    return (
        <Container bannerTheme={bannerTheme} className={className}>
            <Content>
                <HBText type={isMediumUp ? 'display24' : 'title18'}>
                    {title}
                </HBText>
                <Subtitle type="body14">{subtitle}</Subtitle>
                <Actions
                    {...actionProps}
                    actionsSize={isMediumUp ? 'sm' : 'xs'}
                    primaryActionTheme={primaryActionTheme}
                    align="start"
                    testId={finalTestId}
                />
            </Content>
            {illustration && <Illustration>{illustration}</Illustration>}
            {onClose && (
                <CloseButton
                    icon={Close16}
                    label="close"
                    onClick={onClose}
                    testId={
                        finalTestId ? `${finalTestId}-close-button` : undefined
                    }
                />
            )}
        </Container>
    );
};

export default HBPromotionalBanner;

// -------------------------- Styles -------------------------- //

const THEMES = {
    plain: {
        backgroundColor: vars.colors.surface.item
    },
    accent: {
        backgroundColor: vars.colors.surface.accent
    }
} as const;

const Container = styled.div<{ bannerTheme: Props['bannerTheme'] }>`
    display: flex;
    border-radius: ${vars.radius.xs};
    ${({ bannerTheme }) => THEMES[bannerTheme]}
`;

const Content = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    margin: 16px 0 16px 16px;
    ${mediumUp} {
        margin: 20px 0 20px 20px;
    }
`;

const Actions = styled(BannerActions)`
    flex: 1;
    display: flex;
    margin-top: 16px;
    ${mediumUp} {
        margin-top: 20px;
    }
`;

const Subtitle = styled(HBText)`
    margin-top: 8px;
`;

const Illustration = styled.div`
    margin-block: 8px;
    margin-left: 24px;

    img {
        height: 100%;
    }
`;

const CloseButton = styled(HBIconButton)`
    align-self: flex-start;
    margin: 8px 8px 0 16px;
`;
